import { Button, Tooltip } from "components";
import { Card } from "components/atoms/Card";
import { CardItem } from "components/atoms/CardItem";
import { Collapsable } from "components/atoms/Collapsable";
import { Empty } from "components/atoms/Empty";
import Icon from "components/atoms/Icon/Icon";
import Loading from "components/atoms/Loading/Loading";
import React, { useMemo, useState } from "react";
import { useAppSelector } from "store/hooks";
import { UTMSessions, UTMVisits } from "store/slices/leadVisited";
import { LeadProfileDTO } from "utils/dtos/leadDTO";
import EntryFormatter from "utils/EntryFormatter";
import { emptyMessages } from "utils/message";
import { limitSeconds, showDate } from "utils/workWithData";
interface HeaderProps {
  url?: string;
  date?: string;
  duration?: string;
  pageName?: string;
  hiddenLinkIcon?: boolean;
  removeBadge?: boolean;
  isOpen?: boolean;
  hiddenUTMButton: boolean;
}

export const PageVisited = (props: { lead: LeadProfileDTO }) => {
  const { data: list, isLoading } = useAppSelector(
    (state) => state.leadVisited
  );
  return (
    <Card title="Page Visited" className="overflow-y-auto h-full max-h-fit">
      <div className="flex flex-col divide-y divide-gray-700">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loading height="auto" spinnerSize={40} />
          </div>
        ) : list.data.length > 0 ? (
          <>
            <HeaderTable />
            {list.data.map((pulse, index) => (
              <ListContainer
                key={index}
                lead={props.lead}
                pulse={pulse}
                index={index}
                totalItems={list.data.length}
              />
            ))}
          </>
        ) : (
          <Empty message={emptyMessages.websiteTraffic} />
        )}
      </div>
    </Card>
  );
};
const PageTitle: React.FC<{
  isLink?: string;
  pageName: string;
  removeBadge: boolean;
  domain?: string | null;
}> = ({ isLink, pageName, removeBadge, domain }) => {
  const handleNavigate = () => {
    let pageNameTreated = pageName;
    if (!isLink) {
      pageNameTreated = `${domain}${pageName}`;
    }
    let url = EntryFormatter.formatLink(pageNameTreated);
    window.open(url, "_blank");
  };
  if (pageName.length > 60)
    return (
      <Tooltip content={pageName} isFullWidth>
        <p
          onClick={handleNavigate}
          className={`link ${
            !removeBadge ? "badge pointer-events-none " : "!text-white-500"
          } font-bold sm off min-w-[35px] flex items-center gap-2 w-fit`}
        >
          {pageName}
          {removeBadge && <Icon name="url" size={15} />}
        </p>
      </Tooltip>
    );
  return (
    <div
      onClick={handleNavigate}
      className={`link ${
        !removeBadge ? "badge pointer-events-none " : "!text-white-500"
      } font-bold sm off min-w-[35px] flex items-center gap-2 w-fit`}
    >
      {pageName}
      {removeBadge && <Icon name="url" size={15} />}
    </div>
  );
};
const ListContainer: React.FC<{
  pulse: UTMSessions;
  index: number;
  lead: LeadProfileDTO;
  totalItems: number;
}> = ({ pulse, index, lead, totalItems }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasUTM = useMemo(() => {
    return (
      Boolean(
        pulse?.utm?.source ||
          pulse?.utm?.keyword ||
          pulse?.utm?.medium ||
          pulse?.utm?.campaign ||
          pulse?.utm?.referrer
      ) || pulse.utm
    );
  }, [pulse]);
  if (!pulse.visits) {
    //TODO fix this type
    const pulseAny: any = pulse;
    return <VisitItem {...pulseAny} />;
  }
  if (!hasUTM)
    return (
      <>
        {pulse?.visits?.map((visit) => (
          <VisitItem {...visit} key={visit.id} />
        ))}
      </>
    );

  return (
    <Collapsable
      key={pulse.sessionId}
      headerClicked={() => setIsOpen((state) => !state)}
      headerComponent={
        <Header
          hiddenUTMButton={!pulse.utm}
          pageName={`Session ${totalItems - index}`}
          isOpen={isOpen}
          hiddenLinkIcon
          date={showDate(pulse?.visits?.[0]?.dateVisited, false, true)}
          duration={
            pulse?.visits?.[0].timeOnPage
              ? limitSeconds(pulse?.visits?.[0].timeOnPage)
              : undefined
          }
        />
      }
      subHeaderComponent={
        <div
          className={`grid grid-cols-12 col-span-12 md:px-8 px-3 w-full transition-all duration-800 bg-gray-950 overflow-hidden ${
            isOpen
              ? "h-full max-h-[600px] ring-1 ring-gray-800 "
              : "h-0 max-h-0 ring-0"
          }`}
        >
          <div className="col-span-12 w-full">
            <CardItem prefix="UTM Source" value={pulse?.utm?.source} />
          </div>
          <div className="col-span-12 w-full">
            <CardItem prefix="UTM Keyword" value={pulse?.utm?.keyword} />
          </div>
          <div className="col-span-12 w-full">
            <CardItem prefix="UTM Medium" value={pulse?.utm?.medium} />
          </div>
          <div className="col-span-12 w-full">
            <CardItem prefix="Referrer" value={pulse?.utm?.referrer || "-"} />
          </div>
          <div className="col-span-12 w-full">
            <CardItem prefix="UTM Campaign" value={pulse?.utm?.campaign} />
          </div>
        </div>
      }
    >
      <div className="col-span-12 w-full divide-y divide-gray-700">
        {pulse?.visits?.map((visit) => (
          <VisitItem
            {...visit}
            domain={lead.organization?.websiteUrl}
            key={visit.id}
          />
        ))}
      </div>
    </Collapsable>
  );
};
const HeaderTable: React.FC<{}> = () => {
  return (
    <header className="grid grid-cols-12 w-full md:px-4 md:p-2 md:pl-[50px] mb-4 md:mb-0 text-sm col-span-12">
      <div className="col-span-4 md:col-span-8 w-full text-gray-500 flex gap-2">
        <Icon name="globe" size={18} /> URL
      </div>
      <div className="col-span-4 md:col-span-2 w-full flex text-center justify-center text-gray-500 gap-2">
        <Icon name="clock" size={18} /> Duration
      </div>
      <div className="col-span-4 md:col-span-2 w-full flex text-center pr-5 justify-end text-gray-500 gap-2">
        <Icon name="calendar-alt" size={18} /> Date
      </div>
    </header>
  );
};
const Header: React.FC<HeaderProps> = (props) => {
  return (
    <div className="grid  grid-cols-12 w-full  py-4 px-2 md:p-4 pr-0">
      <div className="col-span-4 md:col-span-8 w-full flex flex-row gap-2 pointer-events-none">
        <PageTitle
          isLink={undefined}
          pageName={props.pageName || ""}
          removeBadge={props.removeBadge || false}
        />
        {!props.hiddenUTMButton && (
          <Button
            action="button"
            size="xxs"
            type="download"
            onClick={(event: any) => {
              event.stopPropagation();
            }}
          ></Button>
        )}
      </div>
      {props.duration && (
        <div className="col-span-4 md:col-span-2 w-full text-center flex justify-center">
          <PageTitle
            isLink={props.url}
            pageName={props.duration}
            removeBadge={props.removeBadge || false}
          />
        </div>
      )}
      {props.date && (
        <div className="col-span-4 md:col-span-2 w-full text-right justify-end flex items-center">
          <PageTitle
            isLink={props.url}
            pageName={props.date}
            removeBadge={props.removeBadge || false}
          />
        </div>
      )}
    </div>
  );
};
const VisitItem: React.FC<UTMVisits & { domain?: string | null }> = (props) => {
  return (
    <div className="grid grid-cols-12 w-full py-4 px-2 md:p-4 md:pl-[50px] col-span-12">
      <div className="col-span-4 md:col-span-8 w-full">
        <div className="!text-sm truncate w-full flex items-center gap-2">
          <PageTitle
            pageName={props.pageName || "-"}
            domain={props.domain}
            removeBadge
            isLink={props.url || undefined}
          />
        </div>
      </div>
      <div className="col-span-4 md:col-span-2 w-full text-center justify-center">
        <p className="text-sm truncate w-full !text-white-500 ">
          {" "}
          {props.timeOnPage ? limitSeconds(props.timeOnPage) : "-"}{" "}
        </p>
      </div>
      <div className="col-span-4 md:col-span-2 w-full text-right justify-end">
        <p className="text-sm truncate w-full !text-white-500">
          {" "}
          {showDate(props.dateVisited, false, true)}{" "}
        </p>
      </div>
    </div>
  );
};
